// §25 — Returns export modal. CSV is wired (exportReturnsCsv); Excel / PDF /
// JSON have no bulk backend endpoint (ReturnApi.auditPdf is per-RET only) so
// they render disabled with a "удахгүй" note. The modal recaps the active list
// filters and lets the user pick which CSV columns to include.
import {
  Alert, Box, Button, Checkbox, Group, Modal, Progress, Radio, Stack, Text, Title
} from "@mantine/core";
import { DownloadSimpleIcon, InfoIcon } from "@phosphor-icons/react";
import { showNotification } from "@dehub/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { IReturnListParams } from "../../apis/return";
import {
  downloadBlob, exportReturnsCsv, RETURNS_CSV_COLUMNS, ReturnsCsvColumnKey, IExportProgress
} from "../../utils/export-returns-csv";

const FORMATS: { value: string; label: string; enabled: boolean }[] = [
  { value: "csv",  label: "CSV",   enabled: true  },
  { value: "xlsx", label: "Excel", enabled: false },
  { value: "pdf",  label: "PDF",   enabled: false },
  { value: "json", label: "JSON",  enabled: false }
];

const ALL_KEYS = RETURNS_CSV_COLUMNS.map((c) => c.key);

interface Props {
  opened: boolean;
  onClose: () => void;
  params: IReturnListParams;
  // Human-readable recap of the active filters (label/value pairs).
  recap?: { label: string; value: string }[];
}

export function ReturnsExportModal ({ opened, onClose, params, recap = [] }: Props){
  const { t } = useTranslation("returns");
  const [format, setFormat] = useState("csv");
  const [columns, setColumns] = useState<ReturnsCsvColumnKey[]>(ALL_KEYS);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<IExportProgress | null>(null);

  const onExport = async () => {
    if (format !== "csv" || columns.length === 0) return;
    setBusy(true);
    setProgress(null);
    try {
      const blob = await exportReturnsCsv(params, {
        columns,
        onProgress: (p) => setProgress(p)
      });
      downloadBlob(blob, `returns_${new Date().toISOString().slice(0, 10)}.csv`);
      showNotification({ color: "green", title: t("export.downloaded"), message: t("export.csvDownloaded") });
      onClose();
    } catch (e) {
      showNotification({ color: "red", title: t("toast.error"), message: (e as Error).message });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.current / progress.total) * 100))
    : 0;

  return (
    <Modal opened={opened} onClose={onClose} title={t("export.title")} size={600}>
      <Stack gap="md">
        {/* Format */}
        <Box>
          <Title order={6} mb="xs">{t("export.format")}</Title>
          <Radio.Group value={format} onChange={setFormat}>
            <Group gap="lg">
              {FORMATS.map((f) => (
                <Radio
                  key={f.value}
                  value={f.value}
                  disabled={!f.enabled}
                  label={f.enabled ? f.label : t("export.comingSoonSuffix", { label: f.label })}
                />
              ))}
            </Group>
          </Radio.Group>
        </Box>

        {/* Filter recap */}
        {recap.length > 0 ? (
          <Box>
            <Title order={6} mb="xs">{t("export.filters")}</Title>
            <Stack gap={2}>
              {recap.map((r) => (
                <Group key={r.label} justify="space-between" gap="xs" wrap="nowrap">
                  <Text size="sm" c="dimmed">{r.label}</Text>
                  <Text size="sm" fw={500}>{r.value}</Text>
                </Group>
              ))}
            </Stack>
          </Box>
        ) : null}

        {/* Column picker (CSV only) */}
        {format === "csv" ? (
          <Box>
            <Group justify="space-between" mb="xs">
              <Title order={6}>{t("export.columns")}</Title>
              <Button
                variant="subtle"
                size="compact-xs"
                onClick={() => setColumns(columns.length === ALL_KEYS.length ? [] : ALL_KEYS)}
              >
                {columns.length === ALL_KEYS.length ? t("export.clear") : t("export.selectAll")}
              </Button>
            </Group>
            <Checkbox.Group value={columns} onChange={(v) => setColumns(v as ReturnsCsvColumnKey[])}>
              <Group gap="sm">
                {RETURNS_CSV_COLUMNS.map((c) => (
                  <Checkbox key={c.key} value={c.key} label={c.label} />
                ))}
              </Group>
            </Checkbox.Group>
          </Box>
        ) : (
          <Alert color="blue" variant="light" icon={<InfoIcon size={16} />}>
            {t("export.csvOnly")}
          </Alert>
        )}

        {busy && progress ? (
          <Box>
            <Text size="sm" c="dimmed" mb={4} style={{ fontVariantNumeric: "tabular-nums" }}>
              {t("export.progressRows", { current: progress.current, total: progress.total })}
            </Text>
            <Progress value={pct} radius="sm" animated />
          </Box>
        ) : null}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t("export.close")}</Button>
          <Button
            leftSection={<DownloadSimpleIcon size={16} />}
            onClick={onExport}
            loading={busy}
            disabled={format !== "csv" || columns.length === 0}
          >
            {t("export.download")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
